// One reference image tile: thumbnail + filename + inline-editable
// caption ("Add notes…"). Click the caption row to edit; blur or Save
// commits via PATCH; Escape cancels. Trash icon hovers in the top-right.
import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { api } from "../../../lib/api";
import type { ReferenceImageDto } from "./ReferenceLibrary";

interface Props {
  typeId: number;
  image: ReferenceImageDto;
  onChanged: (next: ReferenceImageDto) => void;
  onDeleted: (id: number) => void;
}

export function ReferenceImageTile({ typeId, image, onChanged, onDeleted }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(image.caption ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveCaption = async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await api<ReferenceImageDto>(
        `/api/tools/pipe-takeoff/references/${typeId}/images/${image.id}`,
        { method: "PATCH", json: { caption: draft || null } },
      );
      onChanged(next);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete image "${image.filename}"?`)) return;
    try {
      await api(
        `/api/tools/pipe-takeoff/references/${typeId}/images/${image.id}`,
        { method: "DELETE" },
      );
      onDeleted(image.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div style={{
      border: "1px solid var(--bsp-border)",
      borderRadius: 6,
      overflow: "hidden",
      background: "var(--bsp-surface)",
      display: "grid",
      gridTemplateRows: "auto auto",
    }}>
      <div style={{ position: "relative", background: "var(--ink-100, #F5F7FA)" }}>
        {image.imageUrl ? (
          <img
            src={image.imageUrl}
            alt={image.filename}
            style={{ display: "block", width: "100%", height: 180, objectFit: "contain" }}
          />
        ) : (
          <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--bsp-fg-muted)", fontSize: 12 }}>
            (no preview)
          </div>
        )}
        <button
          type="button"
          onClick={() => void remove()}
          title="Delete image"
          className="bsp-btn bsp-btn-ghost bsp-btn-icon"
          style={{
            position: "absolute", top: 6, right: 6,
            background: "rgba(255,255,255,0.9)",
            color: "var(--bsp-red-fg)",
          }}
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div style={{ padding: "8px 10px", display: "grid", gap: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 600, wordBreak: "break-all" }}>
          {image.filename}
        </div>

        {editing ? (
          <div style={{ display: "grid", gap: 6 }}>
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              className="bsp-input"
              style={{ fontFamily: "inherit", fontSize: 12, resize: "vertical" }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setDraft(image.caption ?? "");
                  setEditing(false);
                }
              }}
              disabled={busy}
            />
            <div style={{ display: "flex", gap: 6 }}>
              <button type="button" className="bsp-btn bsp-btn-primary bsp-btn-sm"
                onClick={() => void saveCaption()} disabled={busy}>
                {busy ? "Saving…" : "Save"}
              </button>
              <button type="button" className="bsp-btn bsp-btn-ghost bsp-btn-sm"
                onClick={() => { setDraft(image.caption ?? ""); setEditing(false); }}
                disabled={busy}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { setDraft(image.caption ?? ""); setEditing(true); }}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "transparent", border: 0, padding: 0,
              cursor: "pointer", textAlign: "left", font: "inherit",
              color: image.caption ? "inherit" : "var(--bsp-fg-muted)",
              fontSize: 12, fontStyle: image.caption ? "normal" : "italic",
            }}
            title="Edit notes"
          >
            <Pencil size={11} />
            {image.caption || "Add notes…"}
          </button>
        )}

        {error && (
          <p style={{ color: "var(--bsp-red-fg)", margin: 0, fontSize: 11 }}>{error}</p>
        )}
      </div>
    </div>
  );
}
